<template>
    <ul class="filter">
        <!-- Tags Filter -->
        <li class="filter__item" v-if="tags && isCasesPage">
            <div class="filter__item-container">
                <label class="filter__item-item">
                    <input type="radio" name="tag" v-model="selectedTag" :value="null" @change="filterChanged" />
                    {{ dictionary.FilterAll || 'All' }}
                </label>
                <label class="filter__item-item" v-for="tagItem in tags" :key="tagItem.Key">
                    <input type="radio" name="tag" v-model="selectedTag" :value="tagItem.Key" @change="filterChanged" />
                    {{ tagItem.Name }}
                </label>
            </div>
        </li>

        <!-- Types Filter -->
        <li class="filter__item" v-if="types && !isCasesPage">
            <div class="filter__item-container">
                <label class="filter__item-item">
                    <input type="radio" name="type" v-model="selectedType" :value="null" @change="filterChanged" />
                    {{ dictionary.FilterAll || 'All' }}
                </label>
                <label class="filter__item-item" v-for="typeItem in types" :key="typeItem.Key">
                    <input type="radio" name="type" v-model="selectedType" :value="typeItem.Key"
                        @change="filterChanged" />
                    {{ typeItem.Name }}
                </label>
            </div>
        </li>
    </ul>

    <!-- Articles List -->
    <ul class="articles-items" :class="isCasesPage ? '' : 'articles-items__small-wide'">
        <li v-for="article in articles" :key="article.Url" class="articles-items__item">
            <article class="article-item">
                <a :href="article.Url" class="article-item__link">
                    <div class="article-item__inner">
                        <div class="article-item__image">
                            <ResponsiveImage v-if="article.ImageForVue" :imageModel="article.ImageForVue" />
                            <div v-if="article.HasGif" class="article-item__image-gif"
                                :style="{ backgroundImage: `url(${article.Gif})` }">
                            </div>
                            <div v-if="article.HasVideo" class="video-item">
                                <div class="video-item__inner">
                                    <video class="article-item__video" :data-video-url="article.Video"
                                        :src="article.Video" loop autoplay playsinline muted>
                                        <source :src="article.Video" type="video/mp4" />
                                    </video>
                                </div>
                            </div>
                        </div>
                        <div class="article-item__text">
                            <span class="article-item__content-type" v-if="article.HasContentType">
                                {{ article.ContentType }}
                            </span>
                            <h3 class="article-item__headline">{{ article.Headline }}</h3>
                            <p class="article-item__teaser" v-if="article.HasTeaser">
                                {{ article.Teaser }}
                            </p>
                        </div>
                    </div>
                </a>
            </article>
        </li>
    </ul>

    <!-- Load More Button -->
    <div class="articles-items__cta" v-show="hasMoreArticles">
        <a class="articles-items__load-more" @click="getNextPage" :disabled="isLoading">
            {{ dictionary.LoadMore }}
        </a>
    </div>
</template>

<script>
import { bindRoute } from "./mixins/bindroute.js";
import ResponsiveImage from "./components/responsiveImage.vue";
import { customCursor } from "../partials/customCursor.js";

const serviceUrl = "/umbraco/api/articlesApi/getArticles";

export default {
    name: "Articles",
    components: {
        ResponsiveImage,
    },
    props: {
        articlesNodeId: Number,
        pageSize: Number,
        isCasesPage: Boolean
    },
    data() {
        return {
            dictionary: [],
            services: [],
            types: [],
            tags: [],
            articles: [],
            totalArticles: 0,
            page: 1,
            isLoading: false,
            selectedService: null,
            selectedType: null,
            selectedTag: null
        };
    },
    mixins: [
        bindRoute({
            service: null,
            type: null,
            tag: null
        }),
    ],
    watch: {
        service: {
            handler(newVal, oldVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        },
        type: {
            handler(newVal, oldVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        },
        tag: {
            handler(newVal, oldVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        }
    },
    mounted() {
        this.selectedService = this.$route.query.service || null;
        this.selectedType = this.$route.query.type || null;
        this.selectedTag = this.$route.query.tag || null;

        this.fetchArticles(0);
    },
    methods: {
        handleFilterPropertyChange(oldVal, newVal) {
            if (oldVal !== newVal) {
                if (this.hasLoadedInitials && !this.isResetting) {
                    this.filterChanged();
                }
            }
        },
        fetchArticles(itemsToSkip = 0) {
            this.isLoading = true;

            const params = {
                articlesNodeId: this.articlesNodeId,
                cultureCode: document.querySelector("html").getAttribute("lang"),
                itemsToSkip,
                pageSize: this.pageSize,
                isCasesPage: this.isCasesPage,
                tag: this.selectedTag,
                type: this.selectedType,
                service: this.selectedService
            };

            fetch(serviceUrl + "?" + new URLSearchParams(params))
                .then((response) => response.json())
                .then((data) => {
                    if (itemsToSkip > 0) {
                        this.articles.push(...data.Items || []);
                    } else {
                        this.articles = data.Items || [];
                    }
                    this.services = data.Facets?.Services || [];
                    this.types = data.Facets?.Types || [];
                    this.tags = data.Facets?.Tags || [];
                    this.dictionary = data.Dictionary || {};
                    this.totalArticles = data.TotalItems || 0;

                    // Reinitialize customCursor after articles are rendered
                    this.$nextTick(() => {
                        customCursor(".article-item__image");
                    });
                })
                .catch((error) => console.error("Error fetching articles:", error))
                .finally(() => {
                    this.isLoading = false;
                });
        },
        filterChanged() {
            this.page = 1;
            this.articles = [];
            this.fetchArticles(0);
        },
        getNextPage() {
            if (!this.isLoading && this.hasMoreArticles) {
                const itemsToSkip = this.pageSize * this.page;
                this.page++;
                this.fetchArticles(itemsToSkip);
            }
        },
    },
    computed: {
        hasMoreArticles() {
            return this.articles.length < this.totalArticles;
        },
    },
};
</script>